<template>
	<Teleport to="body">
		<Transition name="fade">
			<div v-if="props.modelValue" class="modal">
				<div class="modal-content" :class="props.size">
					<div class="modal-header" :class="[props.customHeaderClass, { 'test-mode': props.testMode }]">
						<h2 class="text-lg font-bold m-0">{{ props.title }}</h2>
						<button class="close-btn" @click="closeModal">&times;</button>
					</div>
					<div class="modal-body">
						<slot name="body">
							<p v-if="props.message">{{ props.message }}</p>
						</slot>
					</div>
					<div class="modal-footer">
						<slot name="footer">
							<button class="btn btn-secondary" @click="closeModal">Cancelar</button>
							<button class="btn btn-primary" @click="closeModal">Confirmar</button>
						</slot>
					</div>
				</div>
			</div>
		</Transition>
	</Teleport>
</template>

<script setup lang="ts">
const props = withDefaults(
	defineProps<{
		modelValue: boolean
		title: string
		message: string
		testMode: boolean
		size: string
		customHeaderClass?: string
	}>(),
	{
		title: 'Modal',
		message: '',
		testMode: false,
		size: 'default',
		customHeaderClass: undefined
	}
)

const emit = defineEmits(['update:modelValue'])

const closeModal = () => {
	emit('update:modelValue', false)
}
</script>

<style scoped>
.modal {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
	padding: 1rem;
	box-sizing: border-box;
}

.modal-content {
	background-color: white;
	border-radius: 8px;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
	width: 90%;
	max-width: 500px;
	max-height: 90vh;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	transition: max-width 0.3s ease;
}

/* Modal Sizes */
.modal-content.small {
	max-width: 400px;
}

.modal-content.default {
	max-width: 500px;
}

.modal-content.large {
	max-width: 700px;
}

.modal-content.xlarge {
	max-width: 900px;
}

.modal-content.xxlarge {
	max-width: 1100px;
}
.modal-content.xxxlarge {
	max-width: 1300px;
}
.modal-content.xxxxlarge {
	max-width: 1500px;
}
.modal-content.xxxxxlarge {
	max-width: 1700px;
}
@media (max-width: 768px) {
	.modal {
		padding: 0.5rem;
	}

	.modal-content,
	.modal-content.small,
	.modal-content.default,
	.modal-content.large,
	.modal-content.xlarge {
		width: 95%;
		max-width: none;
		margin: 0 auto;
	}
}

.modal-header {
	padding: 1rem;
	border-bottom: 1px solid #e2e8f0;
	display: flex;
	justify-content: space-between;
	align-items: center;
	background-color: #fff;
	transition: background-color 0.3s ease;
}

.modal-header.test-mode {
	background-color: #dc3545;
	color: white;
}

.modal-header.test-mode h2,
.modal-header.test-mode .close-btn {
	color: white;
}

.modal-body {
	padding: 1rem;
	overflow-y: auto;
	flex: 1;
}

.modal-footer {
	padding: 1rem;
	border-top: 1px solid #e2e8f0;
	display: flex;
	justify-content: flex-end;
	gap: 0.5rem;
}

.close-btn {
	background: none;
	border: none;
	font-size: 1.5rem;
	cursor: pointer;
	color: #666;
	transition: color 0.3s ease;
}

.close-btn:hover {
	color: #333;
}

.modal-header.test-mode .close-btn:hover {
	color: #ffcdd2;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>
